import { DrawerState } from 'features/rightDrawer'
import React, { useCallback } from 'react'
import {
  createOrganizationSearch,
  editOrganizationSearch,
} from 'routes/organization/-searchParams'
import {
  createSiteSearch,
  editSiteSearch,
} from 'routes/organization_/site/-searchParams'
import { GeckoOrganization, GeckoSite, RolodexEntryType } from 'types'

import { SiteSelectorsState } from './useSiteSelectorsState'

type UseEmbeddedSelectorActionsArgs = {
  resetEmbeddedSelectors: () => void
  setIsPopoverOpen: (open: boolean) => void
  selectedOrganizationId: string | undefined
  navigate: SiteSelectorsState['navigate']
  setOrgAndSiteIds: SiteSelectorsState['setOrgAndSiteIds']
  setDrawerEntry: SiteSelectorsState['setDrawerEntry']
  setDrawerState: SiteSelectorsState['setDrawerState']
}

export const useEmbeddedSelectorActions = ({
  resetEmbeddedSelectors,
  setIsPopoverOpen,
  selectedOrganizationId,
  navigate,
  setOrgAndSiteIds,
  setDrawerEntry,
  setDrawerState,
}: UseEmbeddedSelectorActionsArgs) => {
  const beginEmbeddedAction = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      event.preventDefault()
      resetEmbeddedSelectors()
      setIsPopoverOpen(false)
    },
    [resetEmbeddedSelectors, setIsPopoverOpen]
  )

  const handleAddOrganization = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      beginEmbeddedAction(event)
      await setOrgAndSiteIds({
        organizationId: undefined,
        siteId: undefined,
      })
      navigate({
        to: '/organization',
        search: createOrganizationSearch(),
      })
    },
    [beginEmbeddedAction, navigate, setOrgAndSiteIds]
  )

  const handleAddSite = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()

      if (!selectedOrganizationId) return

      resetEmbeddedSelectors()
      setIsPopoverOpen(false)
      await setOrgAndSiteIds((prev) => ({
        organizationId: prev.organizationId,
        siteId: undefined,
      }))
      navigate({
        to: '/organization/site',
        search: createSiteSearch(selectedOrganizationId),
      })
    },
    [
      navigate,
      resetEmbeddedSelectors,
      selectedOrganizationId,
      setIsPopoverOpen,
      setOrgAndSiteIds,
    ]
  )

  const handleEditOrganization = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement>,
      organization: GeckoOrganization
    ) => {
      beginEmbeddedAction(event)
      setDrawerEntry(organization)
      await setOrgAndSiteIds({
        organizationId: organization.id,
        siteId: undefined,
      })
      navigate({
        to: '/organization',
        search: editOrganizationSearch(organization.id),
      })
    },
    [beginEmbeddedAction, navigate, setDrawerEntry, setOrgAndSiteIds]
  )

  const handleEditSite = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>, site: GeckoSite) => {
      event.stopPropagation()
      event.preventDefault()

      const organizationId = site.tags[RolodexEntryType.ORGANIZATION]

      if (!organizationId) {
        setDrawerState(DrawerState.ErrorState)
        return
      }

      resetEmbeddedSelectors()
      setIsPopoverOpen(false)
      setDrawerEntry(site)
      await setOrgAndSiteIds({
        organizationId,
        siteId: site.id,
      })
      navigate({
        to: '/organization/site',
        search: editSiteSearch(organizationId, site.id),
      })
    },
    [
      navigate,
      resetEmbeddedSelectors,
      setDrawerEntry,
      setDrawerState,
      setIsPopoverOpen,
      setOrgAndSiteIds,
    ]
  )

  return {
    handleAddOrganization,
    handleAddSite,
    handleEditOrganization,
    handleEditSite,
  }
}
